// Document types the backend can parse. Kept here (not just in the <input accept>)
// so a drag-and-drop, which ignores `accept`, can be validated client-side too.
// The file itself is uploaded straight to POST /videos, which parses it server-side.
export const ACCEPTED_EXTENSIONS = ['.pdf', '.docx', '.txt'] as const;

type AcceptedExtension = (typeof ACCEPTED_EXTENSIONS)[number];

// Canonical content type per accepted extension. The backend validates the MIME
// strictly (it must match the extension), but browsers report an empty or
// application/octet-stream type for some files — notably .docx — so we stamp the
// correct type on before upload to avoid a spurious 415. See fileForUpload.
const EXTENSION_CONTENT_TYPE: Record<AcceptedExtension, string> = {
  '.pdf': 'application/pdf',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.txt': 'text/plain',
};

const ACCEPTED_MIME_TYPES = new Set(Object.values(EXTENSION_CONTENT_TYPE));

function acceptedExtension(name: string): AcceptedExtension | undefined {
  const lower = name.toLowerCase();
  return ACCEPTED_EXTENSIONS.find((ext) => lower.endsWith(ext));
}

/**
 * True when the file looks like one the backend can ingest. Checks the extension
 * (authoritative — the backend routes on it) and accepts a known MIME type as a
 * fallback for files the OS reported without a clean extension.
 */
export function isAcceptedFile(file: File): boolean {
  if (acceptedExtension(file.name)) return true;
  return ACCEPTED_MIME_TYPES.has(file.type);
}

/**
 * Returns the file to upload with a canonical Content-Type for its extension, so
 * the multipart part matches the backend's strict MIME check even when the browser
 * reported an empty / application/octet-stream type (common for .docx). Falls back
 * to the original file when the extension isn't recognized or already matches.
 */
export function fileForUpload(file: File): File {
  const ext = acceptedExtension(file.name);
  const type = ext ? EXTENSION_CONTENT_TYPE[ext] : undefined;
  if (!type || file.type === type) return file;
  return new File([file], file.name, { type });
}
